import { Box, Flex, Text, keyframes } from '@chakra-ui/react';

const AVATARS = ['🦊', '🐨', '🐰', '🦁', '🐝', '🐿️', '🐼', '🐧', '🦉', '🐢', '🦄', '🐬'];
const ava = (s = '') => AVATARS[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATARS.length];
// Màu theo thứ tự vào phòng — khớp app (PartyApi.colorOf / seatColor) để nhìn đâu cũng ra một người.
const SEAT = ['#5CE1B0', '#FF8A6B', '#49C8E8', '#C08BFF'];
const pulse = keyframes`0%{box-shadow:0 0 0 0 rgba(255,82,82,.6)}70%{box-shadow:0 0 0 7px rgba(255,82,82,0)}100%{box-shadow:0 0 0 0 rgba(255,82,82,0)}`;
const TAG = {
  active: ['ĐANG ĐẤU', '#FF5252', true],
  open: ['ĐANG CHỜ NGƯỜI VÀO', '#38BDF8', false],
  done: ['KẾT THÚC', '#22C55E', false],
  cancelled: ['ĐÃ HUỶ', '#9CA3AF', false],
};

/**
 * Thẻ trận Đại Chiến Gia Đình (2–4 người). Khác thẻ 1v1 ở chỗ không có "VS":
 * mỗi người một dòng đua, thanh chạy theo số câu của chính họ, người thắng ăn cược × (số người − 1).
 */
export default function PartyCard({ r }) {
  const [label, color, pulsing] = TAG[r.status] || TAG.open;
  // Người thắng lên đầu, còn lại xếp theo số câu chạy được.
  const rows = [...r.players].sort((a, b) => {
    if (a.childId === r.winnerChildId) return -1;
    if (b.childId === r.winnerChildId) return 1;
    return b.hits - a.hits;
  });
  const when = r.endedAt || r.createdAt;
  return (
    <Box position="relative" borderRadius="18px" p="16px" color="#fff" overflow="hidden"
      bg="radial-gradient(120% 120% at 50% -10%,#3a2a7a,#150f2e 65%)" boxShadow="0 16px 40px rgba(0,0,0,.35)">
      <Box position="absolute" inset="0" bg="radial-gradient(300px 120px at 15% 20%,rgba(92,225,176,.22),transparent),radial-gradient(300px 120px at 85% 20%,rgba(255,207,74,.22),transparent)" />
      <Box position="relative">
        <Flex align="center" mb="10px" gap="8px" wrap="wrap">
          <Flex align="center" gap="7px" fontSize="10.5px" fontWeight="800" letterSpacing=".08em"
            bg={`${color}33`} border={`1px solid ${color}80`} px="10px" py="4px" borderRadius="99px">
            {pulsing && <Box w="7px" h="7px" borderRadius="full" bg={color} animation={`${pulse} 1.2s infinite`} />}
            {label}</Flex>
          <Text fontSize="10.5px" fontWeight="800" letterSpacing=".08em" px="9px" py="4px" borderRadius="99px"
            bg="whiteAlpha.200">⚔️ {r.players.length} NGƯỜI</Text>
          {r.status === 'open' && r.code && (
            <Text fontSize="11px" fontWeight="800" opacity="0.85">mã {r.code}</Text>
          )}
          {when && <Text ml="auto" fontSize="11px" opacity="0.7" whiteSpace="nowrap">📅 {new Date(when).toLocaleDateString('vi-VN')}</Text>}
        </Flex>

        <Flex align="baseline" gap="8px" mb="10px">
          <Text fontWeight="850" color="#FFCF4A" fontSize="15px">
            {r.wager} <Box as="span" fontSize="18px" verticalAlign="middle">💎</Box> mỗi người
          </Text>
          <Text ml="auto" fontSize="12px" fontWeight="800" opacity="0.85">
            thắng ăn <Box as="span" color="#FFCF4A">+{r.prize} 💎</Box> · {r.count} câu
          </Text>
        </Flex>

        {rows.map((p) => {
          const seat = SEAT[r.players.findIndex((x) => x.childId === p.childId) % SEAT.length];
          const won = p.childId === r.winnerChildId;
          const lost = r.status === 'done' && !won;
          const frac = Math.max(0, Math.min(100, (p.hits / (r.count || 1)) * 100));
          return (
            <Box key={p.childId} mb="8px" opacity={lost ? 0.72 : 1}>
              <Flex align="center" gap="7px" mb="4px">
                <Flex w="26px" h="26px" borderRadius="full" align="center" justify="center" fontSize="14px"
                  bg="whiteAlpha.200" boxShadow={`0 0 0 2px ${won ? '#FFCF4A' : seat}`}>
                  {p.avatar || ava(p.name)}
                </Flex>
                <Text fontWeight="800" fontSize="13.5px" color={won ? '#FFCF4A' : seat} noOfLines={1}>{p.name}</Text>
                {won && (
                  <Text fontSize="9px" fontWeight="900" letterSpacing="1px" px="8px" py="2px" borderRadius="99px"
                    color="#2A1C06" bg="linear-gradient(90deg,#FFCF4A,#FF9E2C)">VÔ ĐỊCH</Text>
                )}
                <Text ml="auto" fontSize="11.5px" fontWeight="800" opacity="0.9">{p.hits}/{r.count}</Text>
                {r.status === 'done' && (
                  <Text fontSize="11.5px" fontWeight="800" color={won ? '#FFCF4A' : '#FB7185'} minW="52px" textAlign="right">
                    {won ? `+${r.prize}` : `−${r.wager}`} 💎
                  </Text>
                )}
              </Flex>
              <Box h="8px" borderRadius="99px" bg="whiteAlpha.300" overflow="hidden">
                <Box h="100%" w={`${frac}%`} bg={won ? 'linear-gradient(90deg,#FFCF4A,#FF9E2C)' : `linear-gradient(90deg,${seat}aa,${seat})`} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
